package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initUnderAContainerLimitPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/UnderAContainerLimit.md", FrontMatterElement.Builder { addScalar("title", "Ktor on Kotlin/Native under a container limit"); addScalar("searchTitle", "Kotlin/Native memory limit — Ktor RSS, p99 and the two settings that decide them"); addScalar("date", "2026-09-14"); addScalar("description", "A Ktor service that was OOM-killed in every run at 128 MiB now serves 2 000 rps there in 61 MB at a p99 of 5.5 ms. Two changes, neither of them a GC setting."); }))
}

@Page("/blog/under-a-container-limit")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun UnderAContainerLimitPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/native-under-load") {
            org.jetbrains.compose.web.dom.Text("The previous post")
        }
        org.jetbrains.compose.web.dom.Text(" left a Kotlin/Native Ktor service that starts in 28 ms and 20 MiB and falls apart under load, with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("GC.collect()")
        }
        org.jetbrains.compose.web.dom.Text(" on a timer as the only lever I had. This is the follow-up with a proper stand, and the collector turned out to be a bystander in both halves of the problem.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("With one change in Ktor — released as 3.6.0 — and one build option, the same service holds 2 000 requests a second inside a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("128 MiB")
        }
        org.jetbrains.compose.web.dom.Text(" container, ten runs out of ten, at a p99 of ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("5.5 ms")
        }
        org.jetbrains.compose.web.dom.Text(" and a peak of ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("61 MB")
        }
        org.jetbrains.compose.web.dom.Text(". Before the two changes that cell was six OOM kills out of six. For scale, measured in the same cell: the JVM answers in 29.6 ms holding 105 MB, and Go in 2.3 ms holding 6 MB.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("None of this is the runtime's fault in general, and the ladder says so: a bare ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("epoll")
        }
        org.jetbrains.compose.web.dom.Text(" loop with no Ktor at all holds the same 2 000 rps in ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("5 MB")
        }
        org.jetbrains.compose.web.dom.Text(" at a p99 of 1.52 ms, and the rung below HTTP parsing — raw sockets through ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-network")
        }
        org.jetbrains.compose.web.dom.Text(" — goes slow in ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("0 of 12")
        }
        org.jetbrains.compose.web.dom.Text(" runs. Below a thousand requests a second on these four cores, unpatched Kotlin/Native was already the fastest of the three: 2.10 ms against the JVM's 3.60. Everything below is about what changes between there and 2 000.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Both changes were reported upstream, and one of them is now a version number. The Ktor one — ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KTOR-9891") {
            org.jetbrains.compose.web.dom.Text("KTOR-9891")
        }
        org.jetbrains.compose.web.dom.Text(" — is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("fixed and released")
        }
        org.jetbrains.compose.web.dom.Text(": ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/ktorio/ktor/pull/5874") {
            org.jetbrains.compose.web.dom.Text("ktorio/ktor#5874")
        }
        org.jetbrains.compose.web.dom.Text(" shipped in ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("3.6.0")
        }
        org.jetbrains.compose.web.dom.Text(" on 16 September 2026, so on that release the throughput half of this post is an upgrade and nothing else. It was re-measured there rather than taken on trust — ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("On 3.6.0 as released")
        }
        org.jetbrains.compose.web.dom.Text(", below. The runtime one, ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KT-89365") {
            org.jetbrains.compose.web.dom.Text("KT-89365")
        }
        org.jetbrains.compose.web.dom.Text(", was closed on 16 September as a duplicate of ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KT-74834") {
            org.jetbrains.compose.web.dom.Text("KT-74834")
        }
        org.jetbrains.compose.web.dom.Text(", which asks for a way to bound the empty allocator pages a thread keeps and has been open since January 2025. There is a runnable reproducer for the first: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/ktor-9891-repro") {
            org.jetbrains.compose.web.dom.Text("youndie/ktor-9891-repro")
        }
        org.jetbrains.compose.web.dom.Text(" — read its README before running it, because on a machine with two cores online none of this happens.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-stand") }) {
        org.jetbrains.compose.web.dom.Text("The stand")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two dedicated hosts, four cores and 7.6 GiB each, Ubuntu 26.04, subject and generator on separate machines over a private link. Kotlin 2.4.10 ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(" release binaries, Ktor 3.5.2 with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-server-cio")
        }
        org.jetbrains.compose.web.dom.Text(", one route returning one JSON object. k6, 30 s per run, memory bounded by a cgroup and read from it. The release check in ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("On 3.6.0 as released")
        }
        org.jetbrains.compose.web.dom.Text(" is that same pair and that same reproducer on 17 September 2026, with the version in the build file as the only difference between its arms.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One methodological note, because every number below depends on it. At 2 000 rps this service has ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("two speeds")
        }
        org.jetbrains.compose.web.dom.Text(": the same binary on the same machine either answers in single-digit milliseconds or in hundreds, decided in the first five seconds of a run. Across 156 runs there is nothing in between — every p99 is under 70 ms or over 110. A p99 from one run is therefore not a number about the program. Everything here is scored as the share of runs that land in the slow state, twelve runs per arm, arms rotated, with Fisher's exact test.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-the-throughput-goes") }) {
        org.jetbrains.compose.web.dom.Text("Where the throughput goes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Not into the collector. A profile of a slow run puts 32–73 % of all CPU inside one function — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("io.ktor.utils.io.locks.SynchronizedObject#lock")
        }
        org.jetbrains.compose.web.dom.Text(" — and the collector's own functions at one or two per cent. Attaching gdb under load and walking every thread, all 211 frames inside that function have the same two callers: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DefaultPool#borrow")
        }
        org.jetbrains.compose.web.dom.Text(" from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("HttpHeadersMap")
        }
        org.jetbrains.compose.web.dom.Text("'s constructor, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DefaultPool#recycle")
        }
        org.jetbrains.compose.web.dom.Text(" from its ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("release")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("HttpHeadersMap")
        }
        org.jetbrains.compose.web.dom.Text(" — the map CIO parses every request's headers into — keeps its storage in two ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("process-wide")
        }
        org.jetbrains.compose.web.dom.Text(" pools, and on Kotlin/Native ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DefaultPool")
        }
        org.jetbrains.compose.web.dom.Text(" is a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("mutableListOf")
        }
        org.jetbrains.compose.web.dom.Text(" behind one lock taken on every borrow and every recycle. So every HTTP request takes two process-wide locks at least twice each, while the process's hundred-odd threads do the same on the same two atomics.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That lock is not a monitor. On Kotlin/Native it is a CAS loop over an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("AtomicReference<LockState>")
        }
        org.jetbrains.compose.web.dom.Text(" that ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("allocates a new ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("LockState")
            }
            org.jetbrains.compose.web.dom.Text(" on every attempt")
        }
        org.jetbrains.compose.web.dom.Text(", so a lost CAS is an allocation rather than a cheap retry and the cost of an acquisition rises with the number of threads trying. On the JVM the class is erased and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("synchronized")
        }
        org.jetbrains.compose.web.dom.Text(" becomes a monitor, which is why the same Ktor source has no such behaviour there.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It shows up as sensitivity to concurrency rather than to rate. Same binary, same 2 000 rps offered, ten runs per arm:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Connections")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Slow runs")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Requests served in 30 s")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("12")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0/10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.7 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("59 362")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("50")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("7/10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("352 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("48 884")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("200")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10/10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 620 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6 361")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The fix is a deletion, and it is not mine.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kotlinx.atomicfu")
        }
        org.jetbrains.compose.web.dom.Text(" rewrote that lock in ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("0.28.0")
        }
        org.jetbrains.compose.web.dom.Text(" — the owner lives in an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("AtomicLong")
        }
        org.jetbrains.compose.web.dom.Text(", waiters park on a monitor, nothing is allocated. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-io")
        }
        org.jetbrains.compose.web.dom.Text(" already declared atomicfu as an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("api")
        }
        org.jetbrains.compose.web.dom.Text(" dependency and already resolved 0.32.1, so the fixed implementation was on the classpath of every Kotlin/Native Ktor build, behind a copy of the version it replaced. Twelve runs per arm, four builds from the same tree differing in one dependency:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Build")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("200 conn")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Requests")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("50 conn")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3.5.2 as released")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("12/12 slow")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 500 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("7 082")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("7/12 slow")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("310 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("pool made lock-free")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0/12")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("11.3 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("59 924")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0/12")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("7.8 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("lock delegated to atomicfu")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0/12")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8.4 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("59 928")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0/12")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6.5 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("both")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0/12")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8.8 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("60 053")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0/12")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6.4 ms")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Zero of seventy-two patched runs slow against nineteen of twenty-four stock, p = 1.7 × 10⁻¹³. Replacing the lock alone is as good as rewriting the pool and better on p99, which is why the pull request changes the lock. At 2 000 rps and 512 MiB the patched service answers in 6.5 ms where the JVM answers in 15.7.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("on-3-6-0-as-released") }) {
        org.jetbrains.compose.web.dom.Text("On 3.6.0 as released")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Three of those four builds are 3.5.2 with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-io")
        }
        org.jetbrains.compose.web.dom.Text(" swapped out, which is not an artifact anyone will depend on, so the release was measured as an arm of its own. In 3.6.0 the class stays and its three methods delegate to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kotlinx.atomicfu.locks.SynchronizedObject")
        }
        org.jetbrains.compose.web.dom.Text("; the copy of the lock, its ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LockState")
        }
        org.jetbrains.compose.web.dom.Text(" and the native mutex pool behind it are gone from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-io/posix")
        }
        org.jetbrains.compose.web.dom.Text(", and the atomicfu resolved with it moves from 0.32.1 to 0.33.0. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("DefaultPool")
        }
        org.jetbrains.compose.web.dom.Text(" is untouched — the header pools are still process-wide, the lock under them is simply no longer allocating.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Same reproducer, same stand, the two released versions interleaved run for run, twelve rounds each at 200 connections and the same 2 000 rps offered:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Build")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Slow runs")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99 (median)")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Requests served in 30 s")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3.5.2 as released")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("12/12 slow")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1.50 s")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6 850")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3.6.0 as released")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("0/12")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("23.7 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("59 700")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("p = 7.4 × 10⁻⁷, and the version in the build file is the only thing that differs between the arms.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Those p99s do not line up with the table above, and a third arm in the same sweep says why: 3.5.2 with the pull request's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-io")
        }
        org.jetbrains.compose.web.dom.Text(" — the 8.4 ms row — rebuilt and run alongside the release on the same day lands at 23.7 ms against the release's 24.1, 0 of 10 slow in both arms, with the ranges overlapping everywhere. Between sweeps on this stand a fast arm's p99 moves by nearly a factor of three; its slow-run share does not move at all. Compare arms inside one sweep, and across sweeps compare the share.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("if-you-cannot-move-to-3-6-0") }) {
        org.jetbrains.compose.web.dom.Text("If you cannot move to 3.6.0")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On 3.5.2 and earlier two things work. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Cap the connections an instance accepts")
        }
        org.jetbrains.compose.web.dom.Text(" — the table above is the whole argument: twelve connections go slow in 0 of 10 runs at the same offered rate, two hundred in 10 of 10. A reverse proxy in front with a bounded keep-alive pool to the service turns the variable you cannot control into one you can, and costs nothing else. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Or build ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ktor-io")
            }
            org.jetbrains.compose.web.dom.Text(" yourself")
        }
        org.jetbrains.compose.web.dom.Text(" with the three-method change and substitute it; that is what every patched number here was measured on.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-the-memory-goes") }) {
        org.jetbrains.compose.web.dom.Text("Where the memory goes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Also not into the heap. Bounding ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("maxHeapBytes")
        }
        org.jetbrains.compose.web.dom.Text(" at half the container limit — the application reading ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("memory.max")
        }
        org.jetbrains.compose.web.dom.Text(" itself, since the runtime does not — moved the peak by thirteen megabytes and survival not at all: seven survivals of twelve at 256 MiB with the bound, with it at half, and without it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Pooled across 413 surviving runs of every sweep taken, peak RSS against peak ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("thread count")
        }
        org.jetbrains.compose.web.dom.Text(" gives r = 0.994 at 2.96 MB per thread over a 10 MB intercept. The runtime source says why: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CustomAllocator")
        }
        org.jetbrains.compose.web.dom.Text(" is per-thread and holds one ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("FixedBlockPage")
        }
        org.jetbrains.compose.web.dom.Text(" per block-size class plus a 256 KiB next-fit page, and a thread that has touched a size class keeps that page for as long as it lives, occupied or not. No ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("GC")
        }
        org.jetbrains.compose.web.dom.Text(" setting counts them — they are pages, not objects.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xbinary=fixedBlockPageSize")
        }
        org.jetbrains.compose.web.dom.Text(" resizes them; the value is in KiB. Eight runs per arm at 50 connections:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Allocator setting")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Peak at 512 MiB")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("CPU")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("128 MiB")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Peak")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("default")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("268.4 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6.00 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("181 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0/8 alive")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("—")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("—")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("fixedBlockPageSize=16")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("52.8 MB")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.65 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("179 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("8/8")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("48.7 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.46 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("fixedBlockPageSize=4")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("39.5 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("7.13 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("183 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8/8")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("44.1 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("7.07 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("-Xallocator=std")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("24.5 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("9.04 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("219 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8/8")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("24.2 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("9.73 ms")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Fitted per build, the slope is 2.96, 0.66, 0.40 and 0.06 MB per thread. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The 16 KiB page is free")
        }
        org.jetbrains.compose.web.dom.Text(": four times less memory at the same p99 and the same CPU — 5.65 ms against 6.00, 179 % against 181 %, which is noise in both columns. There is no argument for the default in a service with a thread pool.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That slope is also why this table says 48.7 MB at 128 MiB and the lead says 61. Same cell, two sweeps: this one held a median of ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("62 threads")
        }
        org.jetbrains.compose.web.dom.Text(", the ten-run sweep in ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("What to set")
        }
        org.jetbrains.compose.web.dom.Text(" held ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("79")
        }
        org.jetbrains.compose.web.dom.Text(". Seventeen more threads at 0.66 MB each is 11.6 MB, and the gap is 12.7. Quote the larger number when sizing a container — RSS here is a function of how many threads the load happens to spin up, not a constant.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And the thread count is itself a setting, one layer up: capping the dispatcher the CIO engine runs on takes the same service from 67 MB to 36 MB at 200 connections, at a lower p99 and the same CPU. That needs a fork rather than a build option, and it is measured in ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/dispatcher-parallelism-on-kotlin-native") {
            org.jetbrains.compose.web.dom.Text("the next post")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-to-set") }) {
        org.jetbrains.compose.web.dom.Text("What to set")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Ten runs per arm, every arm serving essentially the full offered rate:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Your limit")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Connections")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Set")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Expect")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("512 MiB and up")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("200")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("fixedBlockPageSize=16")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("61 MB, 7.2 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("128 MiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("50")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("fixedBlockPageSize=16")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("61 MB, 5.5 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("64 MiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("50")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("fixedBlockPageSize=4")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("39 MB, 6.3 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("32 MiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("50")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("-Xallocator=std")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("24 MB, 8.4 ms, and 34 % more CPU")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""kotlin {
    linuxX64 {
        binaries.executable {
            binaryOption("fixedBlockPageSize", "16")
        }
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xallocator=std")
        }
        org.jetbrains.compose.web.dom.Text(" is the one real trade here: 2.3 times the p99 and 34 % more CPU at 512 MiB and 200 connections, in exchange for the lowest floor. At that floor the other two columns are Go, unmoved at 6.5 MB and 2.67 ms, and the JVM, which does not run there at all — at 64 MiB it starts, sizes its heap to half the limit and is OOM-killed before serving a request; at 32 MiB it never reaches its ready line. Six runs each.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("All of it is on top of the lock fix, which now means Ktor 3.6.0 or newer. Without it — so on 3.5.2 and earlier — a build at 200 connections serves a ninth of the offered rate whatever its allocator is set to.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(" is the only target measured. The lock lives in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-io/posix")
        }
        org.jetbrains.compose.web.dom.Text(", the source set every native target shares, so the behaviour should be the same on Apple and MinGW targets — should be, not is.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Four cores is the only machine. The core count turned out to matter in a way I cannot explain: with cores taken ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("offline")
        }
        org.jetbrains.compose.web.dom.Text(", so the runtime itself counts fewer, the service goes slow in 0 of 12 runs at two cores and 0 of 12 at three against 15 of 20 at four. Restricting the same four-core machine to two CPUs with a cpuset — which leaves the online count at four — does not help, and the thread count is not the difference either. Something that follows the number of online CPUs decides it, and naming it needs the runtime's own instrumentation rather than a load generator.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One route returning one small JSON object is the only workload. A service whose handlers allocate more will move the numbers; the two mechanisms are about per-request framework overhead and per-thread allocator pages, and neither depends on what the handler does.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("fixedBlockPageSize")
        }
        org.jetbrains.compose.web.dom.Text(" numbers are peaks over 30-second runs at a steady rate. Nothing here says what a day-long process does, and page-cache behaviour is exactly the kind of thing that could differ over hours.")
    }
}
